var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { debouncePromise } from "../../util/debounce";
import DeprecatedSelect from "../../src/View/UI/DeprecatedSelect/DeprecatedSelect";
/**
 * A generic lookup component. Lookup components should utilize this component to implement lookup functionality, as it handles
 * some of the common boilerplate code to implement a lookup component. The ModelType type parameter defines the type of objects
 * that will populate the choices for the lookup. The IDType type parameter defines the type of the identifier that is used to
 * uniquely identify the model (usually a number, but can be a string if it's a guid, for example). This component acts as a layer
 * on top of Select.Async that abstracts things away to make it easier for us to implement, maintain, and enhance our standard lookup
 * controls.
 *
 * @param {LookupGenericProps} props - Required. Props that define how the lookup component's specific implementation should work.
 * @param {string} props.labelKey - Optional, defaults to "name".
 * @param {any} props.valueKey - Optional, defaults to "id".
 */
function LookupGeneric(_a) {
    var { labelKey = "name", valueKey = "id" } = _a, props = __rest(_a, ["labelKey", "valueKey"]);
    // Store the selected options in the state of the component.
    const [selectedOptions, setSelectedOptions] = React.useState([]);
    // A function that fires when the underlying Select has its value changed.
    function onSelectionChanged(newSelectedOptions) {
        // Set the component's internal state to the new selection of items.
        setSelectedOptions(newSelectedOptions);
        // Trigger the provided onChange function on the caller to notify them the selection has changed.
        props.onChange(newSelectedOptions);
    }
    // The fetchItemsById function is intended to be used once - when the component loads for the first time.
    // It is responsible for loading the objects that represent the values selected in this component so we
    // can display them like they should be in the UI. The potential problem with passing in a function as a
    // part of props of this component is that it would be very easy to create an anonymous function, or a
    // function that is re-defined on every render of the parent component, causing the need to re-call that
    // function and re-render THIS component, and that gets us into an infinite loop of loading these items.
    //
    // Therefore, we decided to use the useRef hook to help us here. We are making a BIG (but relatively safe)
    // assumption that the function passed into the fetchItemsById prop will not semantically change between
    // renders. In other words, the actual reference to the function might change, but the behavior (definition)
    // of that function will NOT change between renders. Therefore, since we do not want to automatically
    // re-render and re-call this function when the definition of this function changes, we wrap it in a useRef
    // so we do not have to declare it as a dependency in any useEffect hooks within this component.
    //
    // This allows us to achieve the EXPECTED behavior of this component without leaking the details of how the
    // useEffect hooks work inside this component to the calling components.
    const fetchItemsById = React.useRef(props.fetchItemsById);
    // We do the same thing for defaultIds.
    const defaultIds = React.useRef(props.defaultIds);
    // This handles calling to the provided 'fetch' function to retrieve the items represented by the array of
    // item ids.
    const fetchItemsByIdInternal = React.useCallback(() => __awaiter(this, void 0, void 0, function* () {
        // Call the provided function to retrieve the items represented by the default id's.
        const foundSelectedItems = yield fetchItemsById.current(defaultIds.current);
        // Update the component's internal state so the items are now selected in the lookup component.
        setSelectedOptions(foundSelectedItems);
    }), []);
    // Fetches the options that should be provided when the user begins to use the lookup component.
    function fetchOptionsInternal(search) {
        return __awaiter(this, void 0, void 0, function* () {
            // Fetch the options for the lookup using the provided function.
            const options = yield props.fetchOptions(search);
            // Format the options into a JSON format that the Select component understands.
            return { options };
        });
    }
    // When the component first loads, we want to load up any of the items that are identified in the defaultIds property
    // so we can display them in the lookup as being selected.
    React.useEffect(() => {
        if (defaultIds.current && defaultIds.current.length > 0) {
            fetchItemsByIdInternal();
        }
    }, [fetchItemsByIdInternal]);
    // Render a standard Select.Async component using this wrapper component to implement the appropriate behaviors.
    return (_jsx(DeprecatedSelect.Async, { multi: true, "aria-label": props["aria-label"] || "Lookup", labelKey: labelKey, loadOptions: debouncePromise(fetchOptionsInternal, 300), name: "form-field-name", onChange: onSelectionChanged, 
        // @ts-expect-error - TS2322 - Type '(option: ModelType) => Record<any, any>' is not assignable to type '(arg1?: any) => ReactNode'.
        optionRenderer: props.optionRenderer, 
        // @ts-expect-error - TS2322 - Type '((option: ModelType) => Record<any, any>) | undefined' is not assignable to type '((arg1?: any) => ReactNode) | undefined'.
        valueRenderer: props.valueRenderer, value: selectedOptions, valueKey: valueKey, filterOptions: false }));
}
export default LookupGeneric;
